<script setup lang="ts">
import { ref } from 'vue';

const pushEnabled = ref(false);
const permission = ref<NotificationPermission>('default');

async function requestPermission() {
  if (!('Notification' in window)) return;
  permission.value = await Notification.requestPermission();
  if (permission.value === 'granted') pushEnabled.value = true;
}

const toggles = ref([
  {
    key: 'alerts',
    label: 'Alertes critiques',
    sub: 'Terminaux hors ligne, erreurs de paiement',
    enabled: true,
  },
  {
    key: 'orders',
    label: 'Nouvelles commandes',
    sub: 'Notification à chaque nouvelle commande reçue',
    enabled: true,
  },
  {
    key: 'support',
    label: 'Tickets support',
    sub: 'Nouveaux tickets et réponses reçues',
    enabled: false,
  },
  {
    key: 'activity',
    label: "Journal d'activité",
    sub: 'Modifications importantes dans le système',
    enabled: false,
  },
]);
</script>

<template>
  <div class="settings-page">
    <div class="settings-page__header">
      <h1 class="settings-page__title">Notifications push</h1>
      <p class="settings-page__sub">
        Configurez les alertes que vous souhaitez recevoir.
      </p>
    </div>

    <!-- Permission banner -->
    <div
      v-if="permission !== 'granted'"
      class="perm-banner"
      :class="{ 'perm-banner--denied': permission === 'denied' }"
    >
      <div class="perm-banner__icon">
        <i :class="permission === 'denied' ? 'pi pi-ban' : 'pi pi-bell'" />
      </div>
      <div class="perm-banner__text">
        <p class="perm-banner__title">
          {{
            permission === 'denied'
              ? 'Notifications bloquées'
              : 'Activer les notifications push'
          }}
        </p>
        <p class="perm-banner__sub">
          {{
            permission === 'denied'
              ? 'Autorisez les notifications dans les paramètres de votre navigateur.'
              : 'Autorisez le navigateur à envoyer des notifications pour rester informé en temps réel.'
          }}
        </p>
      </div>
      <button
        v-if="permission !== 'denied'"
        class="perm-banner__btn"
        @click="requestPermission"
      >
        Autoriser
      </button>
    </div>

    <!-- Toggles -->
    <div class="notif-list">
      <div v-for="item in toggles" :key="item.key" class="notif-item">
        <div class="notif-item__info">
          <p class="notif-item__label">{{ item.label }}</p>
          <p class="notif-item__sub">{{ item.sub }}</p>
        </div>
        <button
          class="toggle"
          :class="{ 'toggle--on': item.enabled }"
          role="switch"
          :aria-checked="item.enabled"
          @click="item.enabled = !item.enabled"
        >
          <span class="toggle__thumb" />
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.settings-page {
  max-width: 640px;
  display: flex;
  flex-direction: column;
  gap: 2rem;
  padding: 2.5rem 3rem;
  box-sizing: border-box;
}
@media (max-width: 1023px) {
  .settings-page {
    padding: 1.5rem;
  }
}
@media (max-width: 639px) {
  .settings-page {
    padding: 1rem;
  }
}
.settings-page__header {
}
.settings-page__title {
  margin: 0 0 0.375rem;
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--nxt-on-surface, #191c1d);
}
.settings-page__sub {
  margin: 0;
  font-size: 0.9375rem;
  color: var(--nxt-on-surface-muted, #938282);
}

/* Permission banner */
.perm-banner {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.25rem 1.5rem;
  border-radius: 0.75rem;
  border: 1px solid rgba(0, 0, 0, 0.2);
  background: rgba(0, 0, 0, 0.04);
}
.perm-banner--denied {
  border-color: var(--nxt-outline-variant);
  background: var(--nxt-surface-low, #f3f4f5);
}
.perm-banner__icon {
  width: 2.5rem;
  height: 2.5rem;
  min-width: 2.5rem;
  border-radius: 0.5rem;
  background: rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.125rem;
  color: var(--nxt-primary);
}
.perm-banner--denied .perm-banner__icon {
  background: var(--nxt-surface-mid, #edeeef);
  color: var(--nxt-on-surface-muted);
}
.perm-banner__text {
  flex: 1;
  min-width: 0;
}
.perm-banner__title {
  margin: 0 0 0.25rem;
  font-size: 0.875rem;
  font-weight: 700;
  color: var(--nxt-on-surface, #191c1d);
}
.perm-banner__sub {
  margin: 0;
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-muted, #938282);
  line-height: 1.5;
}
.perm-banner__btn {
  padding: 0.5rem 1.25rem;
  border-radius: 0.5rem;
  border: none;
  background: var(--nxt-primary);
  color: #fff;
  font-size: 0.875rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  white-space: nowrap;
  transition: opacity 0.2s ease;
}
.perm-banner__btn:hover {
  opacity: 0.9;
}

/* Notif list */
.notif-list {
  background: var(--nxt-surface-lowest, #fff);
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.75rem;
  overflow: hidden;
}
.notif-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.5rem;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid var(--nxt-outline-variant);
}
.notif-item:last-child {
  border-bottom: none;
}
.notif-item__info {
  flex: 1;
  min-width: 0;
}
.notif-item__label {
  margin: 0 0 0.25rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--nxt-on-surface, #191c1d);
}
.notif-item__sub {
  margin: 0;
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-muted, #938282);
}

/* Toggle switch */
.toggle {
  width: 3rem;
  height: 1.75rem;
  min-width: 3rem;
  border-radius: 9999px;
  border: none;
  background: var(--nxt-outline-variant);
  cursor: pointer;
  position: relative;
  transition: background 0.25s ease;
  padding: 0;
}
.toggle--on {
  background: var(--nxt-primary);
}
.toggle__thumb {
  position: absolute;
  top: 3px;
  left: 3px;
  width: 1.25rem;
  height: 1.25rem;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.15);
  transition: transform 0.25s ease;
}
.toggle--on .toggle__thumb {
  transform: translateX(1.25rem);
}
</style>
